import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, ArrowLeft, Crown, Loader2, LogOut, Mail } from "lucide-react";
import { toast } from "sonner";
import { seo } from "@/lib/seo";
import { useSession } from "@/hooks/use-session";
import { useAuth } from "@/hooks/use-auth";
import { useEntitlement } from "@/hooks/use-entitlement";
import { deleteMyAccount } from "@/lib/profile.functions";

export const Route = createFileRoute("/profile/settings")({
  head: () =>
    seo({
      title: "Settings — The Quantum Minds",
      description: "Your plan details, account settings and account removal.",
      noindex: true,
    }),
  component: SettingsPage,
});

const PLAN_LABEL: Record<string, string> = {
  none: "Signed out",
  guest: "Guest",
  trial: "Free",
  paid: "Premium",
  expired: "Premium expired",
};

function formatDate(value: string | null) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

function SettingsPage() {
  const { profile } = useSession();
  const { signOut } = useAuth();
  const { entitlement, expiresAt } = useEntitlement();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const expiry = formatDate(expiresAt);

  const remove = async () => {
    setDeleting(true);
    try {
      await deleteMyAccount({});
      toast.success("Your account has been deleted.");
      await signOut();
    } catch {
      toast.error("Could not delete your account. Please try again.");
      setDeleting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl px-5 pb-32 pt-8">
      <div className="flex items-center gap-3">
        <Link
          to="/profile"
          aria-label="Back to profile"
          className="glass grid size-10 place-items-center rounded-full"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Settings</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Plan details and account controls.
          </p>
        </div>
      </div>

      {/* PLAN DETAILS */}
      <h2 className="mt-8 px-1 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
        Plan details
      </h2>
      <div className="glass relative mt-3 overflow-hidden rounded-[24px] border-primary/25 p-5">
        <div className="aurora absolute inset-0 opacity-70" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-[image:var(--gradient-brand)] shadow-[var(--shadow-glow)]">
              <Crown size={20} className="text-primary-foreground" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
                Current plan
              </p>
              <p className="mt-1 text-base font-semibold">
                {PLAN_LABEL[entitlement.access_level] ?? "Free"}
              </p>
            </div>
          </div>
          <dl className="mt-4 space-y-2 text-sm">
            {expiry ? (
              <div className="flex items-center justify-between gap-3">
                <dt className="text-muted-foreground">Valid until</dt>
                <dd className="font-medium">{expiry}</dd>
              </div>
            ) : null}
            <div className="flex items-center justify-between gap-3">
              <dt className="text-muted-foreground">Simulation credits</dt>
              <dd className="font-medium">{entitlement.simulation_credits}</dd>
            </div>
          </dl>
          <button
            type="button"
            onClick={() => navigate({ to: "/pricing" })}
            className="mt-5 w-full rounded-2xl bg-[image:var(--gradient-brand)] px-5 py-3.5 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition active:scale-[0.99]"
          >
            {entitlement.paid_active ? "View plans" : "Explore Premium Plans"}
          </button>
        </div>
      </div>

      {/* ACCOUNT */}
      <h2 className="mt-8 px-1 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
        Account
      </h2>
      <div className="glass mt-3 overflow-hidden rounded-[24px]">
        <div className="flex items-center gap-4 border-b border-hairline px-5 py-5">
          <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-surface">
            <Mail size={20} className="text-primary" />
          </span>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Email</p>
            <p className="mt-1 truncate text-sm">{profile?.email || "Your account"}</p>
          </div>
        </div>
        <button
          onClick={signOut}
          className="flex w-full items-center gap-4 px-5 py-5 text-left transition hover:bg-surface"
        >
          <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-surface">
            <LogOut size={20} className="text-primary" />
          </span>
          <span className="text-sm font-medium">Sign out</span>
        </button>
      </div>

      {/* DANGER ZONE */}
      <h2 className="mt-8 px-1 text-[11px] font-medium uppercase tracking-[0.18em] text-destructive">
        Danger zone
      </h2>
      <div className="glass mt-3 rounded-[24px] border-destructive/40 p-5">
        <div className="flex items-start gap-3">
          <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-destructive/10">
            <AlertTriangle size={20} className="text-destructive" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold">Delete account</p>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              This permanently removes your account and all your tracked progress. This
              cannot be undone.
            </p>
          </div>
        </div>

        {confirming ? (
          <div className="mt-5 space-y-3">
            <p className="text-xs font-medium text-destructive">
              Are you sure? This is permanent.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={deleting}
                className="glass rounded-2xl px-4 py-3 text-sm font-medium transition active:scale-[0.99]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={remove}
                disabled={deleting}
                className="flex items-center justify-center gap-2 rounded-2xl bg-destructive px-4 py-3 text-sm font-semibold text-destructive-foreground transition active:scale-[0.99] disabled:opacity-60"
              >
                {deleting ? <Loader2 className="size-4 animate-spin" /> : null}
                Yes, delete
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="mt-5 w-full rounded-2xl border border-destructive/40 px-4 py-3 text-sm font-semibold text-destructive transition hover:bg-destructive/10 active:scale-[0.99]"
          >
            Delete account
          </button>
        )}
      </div>
    </div>
  );
}
